import { useState } from "react";
import { getContainer } from "../../../ioc/IOCSetup";
import { CardBox, LoadingStatus, useLoadingState } from "../../RouterView";
import { BudgetPermissionLimits, PermissionCategory } from "../../../http/protocol";
import { PermissionRequired } from "../../security/PermissionRequired";
import { t } from "../../../i18n";
import BudgetPermissionsTable from "./BudgetPermissionLimitsTable";
import "./budgetPermissionLimitsCard.scss";

export type PermissionLimitType = "BUDGET_SMALL" | "BUDGET_MEDIUM" | "BUDGET_LARGE"

const CATEGORY_KEYS: Partial<Record<PermissionLimitType, keyof BudgetPermissionLimits>> = {
    [PermissionCategory.BUDGET_SMALL]: "budgetSmall",
    [PermissionCategory.BUDGET_MEDIUM]: "budgetMedium",
    [PermissionCategory.BUDGET_LARGE]: "budgetLarge",
};

// Check that values increase in order: small ≤ medium ≤ large.
// A value of 0 is interpreted as "not set" and is skipped in the comparison.
const checkLimitOrder = (lower: number, upper: number, lowerCategory: PermissionCategory, upperCategory: PermissionCategory, label: string): string | undefined => {
    if (lower > 0 && upper > 0 && lower > upper) {
        return t("budgetPermissionLimits.validation.ordering", { label, lower: lowerCategory, upper: upperCategory });
    }
};

function validateLimits({ budgetSmall, budgetMedium, budgetLarge }: BudgetPermissionLimits): string | undefined {
    // Check that no values are negative
    if (
        (budgetSmall?.dailyLimit ?? 0) < 0 || (budgetSmall?.largeAmount ?? 0) < 0 ||
        (budgetMedium?.dailyLimit ?? 0) < 0 || (budgetMedium?.largeAmount ?? 0) < 0 ||
        (budgetLarge?.dailyLimit ?? 0) < 0 || (budgetLarge?.largeAmount ?? 0) < 0
    ) {
        return t("budgetPermissionLimits.validation.nonNegative");
    }

    const dailyLabel = t("budgetPermissionLimits.dailyLimit");
    const largeLabel = t("budgetPermissionLimits.largeAmount");
    const [small, medium, large] = [PermissionCategory.BUDGET_SMALL, PermissionCategory.BUDGET_MEDIUM, PermissionCategory.BUDGET_LARGE];

    // Check all three pairs (small–medium, small–large, medium–large) for both fields.
    // Adjacent pairs alone are insufficient: if the middle category is unset (0),
    // a violation between small and large would be missed.
    return (
        checkLimitOrder(budgetSmall?.dailyLimit ?? 0, budgetMedium?.dailyLimit ?? 0, small, medium, dailyLabel) ??
        checkLimitOrder(budgetSmall?.dailyLimit ?? 0, budgetLarge?.dailyLimit ?? 0, small, large, dailyLabel) ??
        checkLimitOrder(budgetMedium?.dailyLimit ?? 0, budgetLarge?.dailyLimit ?? 0, medium, large, dailyLabel) ??
        checkLimitOrder(budgetSmall?.largeAmount ?? 0, budgetMedium?.largeAmount ?? 0, small, medium, largeLabel) ??
        checkLimitOrder(budgetSmall?.largeAmount ?? 0, budgetLarge?.largeAmount ?? 0, small, large, largeLabel) ??
        checkLimitOrder(budgetMedium?.largeAmount ?? 0, budgetLarge?.largeAmount ?? 0, medium, large, largeLabel)
    );
}

export function BudgetPermissionLimitsCard(): JSX.Element {
    const [limits, setLimits] = useState<BudgetPermissionLimits>({});
    const [saving, setSaving] = useState(false);
    const [validationError, setValidationError] = useState<string | undefined>();
    const [saveError, setSaveError] = useState<string | undefined>();
    const [saveSuccess, setSaveSuccess] = useState(false);

    const status = useLoadingState(() => {
        return getContainer().getPermissionLimitService().getPermissionLimits().then(fetchedLimits => {
            setLimits(fetchedLimits);
        });
    }, []);

    const updateField = (category: PermissionLimitType, field: "dailyLimit" | "largeAmount", value: number): void => {
        const key = CATEGORY_KEYS[category]!;
        setLimits(currentLimits => ({
            ...currentLimits,
            [key]: {
                ...currentLimits[key],
                permissionCategory: category,
                [field]: value,
            }
        }));
        setValidationError(undefined);
        setSaveSuccess(false);
        setSaveError(undefined);
    };

    const handleSave = async (): Promise<void> => {
        const error = validateLimits(limits);
        if (error) {
            setValidationError(error);
            return;
        }
        setSaving(true);
        setSaveError(undefined);
        setSaveSuccess(false);
        try {
            const updatedLimits = await getContainer().getPermissionLimitService().updatePermissionLimits(limits);
            setLimits(updatedLimits);
            setSaveSuccess(true);
        } catch {
            setSaveError(t("budgetPermissionLimits.saveError"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <PermissionRequired permissions={[PermissionCategory.BUDGET_SETTINGS]} accessLevel={"w"}>
            <CardBox title={t("budgetPermissionLimits.title")}>
                {status === LoadingStatus.LOADING && (
                    <div className={"text-center"}><i className={"fa fa-spinner fa-spin"} /></div>
                )}
                {status === LoadingStatus.ERROR && (
                    <div className={"alert alert-danger"}>{t("common.error-table")}</div>
                )}
                {status === LoadingStatus.DONE && (
                    <>
                        <span>{t("budgetPermissionLimits.orderingInfo")}</span>
                        <BudgetPermissionsTable
                            limits={limits}
                            updateField={updateField}
                        />
                        {validationError && (
                            <div className={"alert alert-danger"}>{validationError}</div>
                        )}
                        {saveError && (
                            <div className={"alert alert-danger"}>{saveError}</div>
                        )}
                        {saveSuccess && (
                            <div className={"alert alert-success"}>{t("budgetPermissionLimits.saveSuccess")}</div>
                        )}
                        <button className={"btn btn-primary"} onClick={handleSave} disabled={saving}>
                            {saving ? t("budgetPermissionLimits.saving") : t("button.save")}
                        </button>
                    </>
                )}
            </CardBox>
        </PermissionRequired>
    );
}
